import { ExperimentOutlined } from '@ant-design/icons';
import { Card, List, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { SEVERITY_COLORS } from '@/constants/dashboard';
import type { RecentScanItem } from '@/types';

const { Text } = Typography;

interface RecentAiScansProps {
	scans: RecentScanItem[];
	loading?: boolean;
}

const RecentAiScans = ({ scans, loading = false }: RecentAiScansProps) => {
	const { t } = useTranslation('components');

	const getSeverityColor = (severity: string): string => {
		return SEVERITY_COLORS[severity as keyof typeof SEVERITY_COLORS] ?? '#d9d9d9';
	};

	return (
		<Card
			title={
				<div className='flex items-center gap-2'>
					<ExperimentOutlined className='text-purple-600' />
					<span className='font-semibold text-slate-700'>
						{t('dashboard.recentScans.title')}
					</span>
				</div>
			}
			className='h-full border border-slate-100 shadow-sm rounded-xl'
			styles={{
				body: { padding: '0 16px 16px' },
				header: { borderBottom: '1px solid #f1f5f9', padding: '0 16px' },
			}}
			loading={loading}
		>
			{scans.length > 0 ? (
				<List
					itemLayout='horizontal'
					dataSource={scans}
					rowKey='id'
					className='w-full'
					renderItem={(scan) => (
						<List.Item className='!px-0 !py-3 !border-b-slate-50 last:!border-b-0'>
							<div className='flex items-start gap-3 w-full'>
								<div className='w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0'>
									<ExperimentOutlined className='text-purple-600 text-sm' />
								</div>
								<div className='flex-1 min-w-0'>
									<div className='flex justify-between items-start gap-2'>
										<div>
											<span className='font-medium text-slate-700 text-sm capitalize'>
												{scan.scanType} scan
											</span>
											{scan.petName && (
												<Text className='text-xs text-slate-400 block'>
													{scan.petName}
													{scan.userName ? ` · ${scan.userName}` : ''}
												</Text>
											)}
										</div>
										<div className='flex flex-col items-end gap-1'>
											<Tag color={getSeverityColor(scan.severity)} className='!m-0 capitalize'>
												{scan.severity}
											</Tag>
											<span className='text-xs text-slate-400 whitespace-nowrap'>
												{new Date(scan.timestamp).toLocaleString()}
											</span>
										</div>
									</div>
									{scan.vetConsultationRecommended && (
										<Tag color='red' className='!mt-1 !text-xs'>
											{t('dashboard.recentScans.vetRecommended')}
										</Tag>
									)}
								</div>
							</div>
						</List.Item>
					)}
				/>
			) : (
				<div className='py-8 text-center'>
					<ExperimentOutlined className='text-2xl text-slate-300 mb-2' />
					<Text className='text-sm text-slate-400 block'>
						{t('dashboard.recentScans.noData')}
					</Text>
				</div>
			)}
		</Card>
	);
};

export { RecentAiScans };
export default RecentAiScans;
